import { useId, useMemo, useState } from 'react'

import type { LibraryFolder } from '../../api/library'
import { Button } from '../../ui/Control'

/**
 * Choosing one folder out of the library's tree.
 *
 * It replaces a `<select>` that listed every folder in the library by its **bare name**, flat and in
 * whatever order they arrived. That is unusable the moment a library has more than one `Klausuren`,
 * which is the ordinary case rather than the awkward one: the whole point of folders is that the same
 * word means different things in different places, and a list of bare names throws away precisely the
 * part that tells them apart. Anchoring the wrong folder is not a small mistake either — everything
 * in it and below it belongs to the subject afterwards.
 *
 * So the tree is shown as a tree: indented, parents before children, each branch openable, and every
 * row saying where it is. Two things make it usable rather than merely correct.
 *
 * **Typing searches the whole library, and the answer keeps its path.** A search does not flatten the
 * tree back into the list this replaced — a hit is shown with the folders it lies in written above it
 * in small type, so `Klausuren` is still answerable.
 *
 * **What cannot be chosen is shown anyway, greyed.** A folder already anchored is not missing from
 * the tree; it says it is already there. A tree with holes in it is a tree somebody stops trusting,
 * and the holes here would fall exactly where somebody looks twice.
 */
export interface FolderPickerProps {
  /** The whole tree, as the library describes it. */
  readonly folders: readonly LibraryFolder[]
  /** Which ones are spoken for, and what to say about them. */
  readonly takenIds: readonly string[]
  readonly takenLabel: string
  readonly label: string
  readonly onChoose: (folderId: string) => void
}

export function FolderPicker({
  folders,
  takenIds,
  takenLabel,
  label,
  onChoose,
}: FolderPickerProps) {
  const base = useId()
  const [wanted, setWanted] = useState('')
  const [opened, setOpened] = useState<readonly string[]>([])

  const byParent = useMemo(() => groupedByParent(folders), [folders])
  const pathOf = useMemo(() => pathsOf(folders), [folders])

  const looking = wanted.trim().toLowerCase()
  const hits =
    looking === ''
      ? []
      : folders
          .filter((one) => (pathOf.get(one.id) ?? one.name).toLowerCase().includes(looking))
          .slice(0, MOST_HITS)

  const rows = looking === '' ? laidOut(byParent, null, 0, opened) : []

  return (
    <div className="folder-picker">
      <label className="subject-detail__label" htmlFor={`${base}-search`}>
        {label}
      </label>
      <input
        id={`${base}-search`}
        className="field__control"
        type="search"
        value={wanted}
        placeholder="Find a folder by name or path…"
        onChange={(event) => {
          setWanted(event.target.value)
        }}
      />

      <ul className="folder-picker__tree">
        {looking === ''
          ? rows.map((row) => (
              <FolderRow
                key={row.folder.id}
                folder={row.folder}
                depth={row.depth}
                path={pathOf.get(row.folder.id) ?? row.folder.name}
                hasChildren={(byParent.get(row.folder.id) ?? []).length > 0}
                open={opened.includes(row.folder.id)}
                taken={takenIds.includes(row.folder.id)}
                takenLabel={takenLabel}
                onToggle={() => {
                  setOpened((before) =>
                    before.includes(row.folder.id)
                      ? before.filter((one) => one !== row.folder.id)
                      : [...before, row.folder.id],
                  )
                }}
                onChoose={() => {
                  onChoose(row.folder.id)
                  setWanted('')
                }}
              />
            ))
          : hits.map((one) => (
              <FolderRow
                key={one.id}
                folder={one}
                depth={0}
                path={pathOf.get(one.id) ?? one.name}
                hasChildren={false}
                open={false}
                taken={takenIds.includes(one.id)}
                takenLabel={takenLabel}
                showPath
                onToggle={() => undefined}
                onChoose={() => {
                  onChoose(one.id)
                  setWanted('')
                }}
              />
            ))}

        {looking !== '' && hits.length === 0 && (
          <li className="folder-picker__nothing">No folder is called that.</li>
        )}
        {looking === '' && rows.length === 0 && (
          <li className="folder-picker__nothing">
            There are no folders in your library yet. Make one there and it can be anchored here.
          </li>
        )}
      </ul>
    </div>
  )
}

function FolderRow({
  folder,
  depth,
  path,
  hasChildren,
  open,
  taken,
  takenLabel,
  showPath = false,
  onToggle,
  onChoose,
}: {
  readonly folder: LibraryFolder
  readonly depth: number
  readonly path: string
  readonly hasChildren: boolean
  readonly open: boolean
  readonly taken: boolean
  readonly takenLabel: string
  readonly showPath?: boolean
  readonly onToggle: () => void
  readonly onChoose: () => void
}) {
  const above = path.slice(0, Math.max(0, path.length - folder.name.length)).replace(/\/$/, '')

  return (
    <li
      className="folder-picker__row"
      style={{ '--depth': depth } as React.CSSProperties}
      data-taken={taken}
    >
      {/*
        The twisty and the folder are two controls, because they do two things: one opens a branch,
        the other chooses it. A single row that did both would make choosing a parent impossible
        without walking into it.
      */}
      {hasChildren ? (
        <button
          type="button"
          className="folder-picker__twisty"
          aria-expanded={open}
          aria-label={`${open ? 'Close' : 'Open'} ${folder.name}`}
          onClick={onToggle}
        >
          <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        </button>
      ) : (
        <span className="folder-picker__twisty" aria-hidden="true" />
      )}

      <span className="folder-picker__what">
        <span className="folder-picker__name">{folder.name}</span>
        {showPath && above !== '' && <span className="folder-picker__where">in {above}</span>}
      </span>

      {taken ? (
        <span className="folder-picker__taken">{takenLabel}</span>
      ) : (
        <Button tone="bare" aria-label={`Anchor ${path}`} onClick={onChoose}>
          Anchor
        </Button>
      )}
    </li>
  )
}

/** How many hits a search shows. Enough to find one, few enough to stay a list. */
const MOST_HITS = 40

function groupedByParent(folders: readonly LibraryFolder[]): Map<string | null, LibraryFolder[]> {
  const byParent = new Map<string | null, LibraryFolder[]>()
  for (const folder of folders) {
    const at = byParent.get(folder.parentId) ?? []
    at.push(folder)
    byParent.set(folder.parentId, at)
  }
  for (const at of byParent.values()) {
    at.sort((one, other) => one.name.localeCompare(other.name))
  }
  return byParent
}

/**
 * The whole path of every folder, built from the tree rather than asked for.
 *
 * `parentId` is already on every folder, so the path is arithmetic and needs no second request — and
 * one built here cannot disagree with the tree it is drawn beside.
 */
function pathsOf(folders: readonly LibraryFolder[]): Map<string, string> {
  const byId = new Map(folders.map((one) => [one.id, one]))
  const paths = new Map<string, string>()

  const pathOf = (folder: LibraryFolder, seen: Set<string>): string => {
    const already = paths.get(folder.id)
    if (already !== undefined) return already
    // A cycle cannot happen through the service, and a screen that hung on one would be worse than
    // one that shows a short path.
    if (seen.has(folder.id)) return folder.name
    seen.add(folder.id)
    const parent = folder.parentId === null ? undefined : byId.get(folder.parentId)
    const path = parent === undefined ? folder.name : `${pathOf(parent, seen)}/${folder.name}`
    paths.set(folder.id, path)
    return path
  }

  for (const folder of folders) pathOf(folder, new Set())
  return paths
}

/** The tree flattened for drawing: parents before their children, and only what is open. */
function laidOut(
  byParent: Map<string | null, LibraryFolder[]>,
  parentId: string | null,
  depth: number,
  opened: readonly string[],
): { folder: LibraryFolder; depth: number }[] {
  return (byParent.get(parentId) ?? []).flatMap((folder) => [
    { folder, depth },
    ...(opened.includes(folder.id) ? laidOut(byParent, folder.id, depth + 1, opened) : []),
  ])
}
